import { AuthedLayout } from "@/layouts";
import { type NextPage } from "next";
import { NextSeo } from "next-seo";
import { Box } from "@/components/essentials/box";
import { Typography } from "@/components/essentials/typography";
import { useApolloError } from "@/hooks/useApolloError";
import { useMePagesSettingsAccountToyCouponQuery } from "./__gql__/me.gen";
import { getToyCouponStatus } from "./module";
import { Center, Stack } from "@chakra-ui/react";
import Link from "next/link";
import { Skeleton } from "@/components/fragments/apollo/loader/skeleton";
import {
  skeletonAvailableToyCouponsTestId,
  skeletonUnavailableToyCouponsTestId,
  typographyNotExistAvailableToyCouponsTestId,
  typographyNotExistUnavailableToyCouponsTestId,
} from "./const";
import { AvailableToyCouponTable } from "./__components__/available-toy-coupon-table";
import { UnavailableToyCouponTable } from "./__components__/unavailable-toy-coupon-table";
import { Button } from "@/components/essentials/button";

const ToyCouponPage: NextPage = () => {
  const resultPagesSettingsAccountToyCouponQuery = useMePagesSettingsAccountToyCouponQuery();
  useApolloError(resultPagesSettingsAccountToyCouponQuery.error);
  const availableToyCoupons = resultPagesSettingsAccountToyCouponQuery.data?.me.toyCoupons.filter(
    (toyCoupon) => getToyCouponStatus(toyCoupon) === "available",
  );

  const unavailableToyCoupons = resultPagesSettingsAccountToyCouponQuery.data?.me.toyCoupons.filter(
    (toyCoupon) => getToyCouponStatus(toyCoupon) !== "available",
  );
  return (
    <>
      <NextSeo title="おもちゃ買い取りクーポン一覧" description="おもちゃ買い取りクーポン一覧が見られます。" />
      <AuthedLayout py="1.5rem" px="1rem" bgColor="light.main">
        <Typography variant="display2" mb={7} textAlign="center">
          おもちゃ買取りクーポン一覧
        </Typography>
        <Typography variant="display6" mb={1}>
          ご利用いただけるクーポン
        </Typography>
        <Typography variant="text4" mb={6}>
          おもちゃの買取り時にご利用頂けます。ご利用の際は、交換申請フォームの所定フォーム内にクーポンコードを記載ください。
        </Typography>
        <Box mb={10}>
          {resultPagesSettingsAccountToyCouponQuery.loading ? (
            <Center py="6" w="100%">
              <Skeleton h="10rem" w="100%" data-testid={skeletonAvailableToyCouponsTestId} />
            </Center>
          ) : availableToyCoupons?.length === 0 ? (
            <Typography
              variant="display2"
              py={6}
              textAlign="center"
              data-testid={typographyNotExistAvailableToyCouponsTestId}
            >
              表示できるクーポンがありませんでした。
            </Typography>
          ) : (
            <Stack spacing={6}>
              {availableToyCoupons?.map((toyCoupon, index) => (
                <AvailableToyCouponTable {...toyCoupon} key={index} />
              ))}
            </Stack>
          )}
        </Box>
        <Typography variant="display6" mb={1}>
          使用済み / 失効したクーポン
        </Typography>
        <Typography variant="text4" mb={6}>
          すでにご利用済み、もしくは有効期限超過により失効したクーポンです。
        </Typography>
        <Box mb={10}>
          {resultPagesSettingsAccountToyCouponQuery.loading ? (
            <Center py="6" w="100%">
              <Skeleton data-testid={skeletonUnavailableToyCouponsTestId} h="10rem" w="100%" />
            </Center>
          ) : unavailableToyCoupons?.length === 0 ? (
            <Typography
              variant="display2"
              py={6}
              textAlign="center"
              data-testid={typographyNotExistUnavailableToyCouponsTestId}
            >
              表示できるクーポンがありませんでした。
            </Typography>
          ) : (
            <Stack spacing={6}>
              {unavailableToyCoupons?.map((toyCoupon, index) => (
                <UnavailableToyCouponTable {...toyCoupon} key={index} />
              ))}
            </Stack>
          )}
        </Box>
        <Button as={Link} href="/" w="100%" size="lg" styleVariant="mono" mt={10} mb={20}>
          マイページTOPへ
        </Button>
      </AuthedLayout>
    </>
  );
};

export default ToyCouponPage;
